// LR1 — THE LIVING ROOM, MEMBER SIDE.
//
// The feed, the composer, a thread, comments, likes, @tags, reporting, blocking, edit and delete.
// In its own file rather than App.tsx because an owner round that adds a whole room to a file
// already at its ceiling is exactly the round that gets the ceiling waived, and this one does not
// need it.
//
// THE ONE THING TO KEEP TRUE IN HERE: **A MEMBER'S SCREEN DOES NOT KNOW WHAT SHADOW-HIDE IS.** A
// held post arrives in its author's own feed looking exactly like every other post, and nothing on
// this page can tell her otherwise: the server sends her `visibility: "public"` and no reason at all
// (`shapePost`/`shapeComment`), so there is no field for a render site to leak. If you are about to
// add a "your post is under review" affordance for a MEMBER: that is ruling 1, and the answer is no.
// The same goes for a failing EDIT (LR1-16) — the author sees her own draft, nobody else does, and
// nothing says so.
//
// **LR19 (2026-08-07, her ruling) NARROWED THAT SENTENCE FROM "THIS COMPONENT" TO "A MEMBER'S
// SCREEN", AND THE OLD WORDING IS REPLACED HERE RATHER THAN LEFT STANDING BESIDE IT** (DL-2/R181: an
// override written below leaves the retired law in the source and the next reader cannot tell which
// one the site obeys — this house has enforced six superseded rulings that way). Her report: a held
// comment rendered NORMAL on her own seat, so she read it as published and could only discover the
// hold by leaving for the dashboard. **On an OWNER account and on no other, a held item now wears a
// yellow strip with its reason and her two inline actions.** The decision is not made in this file:
// `heldTreatment(item, viewer)` is the shared law, it asks `viewer.owner === true` FIRST, and a
// non-owner gets `null` no matter what a payload says. So "members see zero change" is a property of
// a function a gate RUNS over every combination, not a promise about a render site below.
import { LiveRail } from "./LiveRail";
import { useCallback, useEffect, useRef, useState } from "react";
import { sharedRoomRead } from "../lib/room-me";
import {
  ArrowLeft, Ban, ChevronDown, FileText, Flag, ImagePlus, MessageCircle, Pencil, Pin, Send,
  Search, Share2, Sparkles, ThumbsUp, Trash2, Video, X,
} from "lucide-react";
import {
  ROOM_TITLE, EMPTY_FEED, EMPTY_SPELLS, COMPOSER_PLACEHOLDER, ROOM_BANNER, ROOM_BANNER_SUB, SPELL_KIND,
  // HER ORDER 2026-09-30: PDFs and books, up to 25MB. The tray law and the card's words, the room's own.
  postDocumentRefusal, documentCardLabel, documentOpensInPlace, postDocumentAccept,
  SPELLS_SECTION_LABEL, SHARE_SPELL_LABEL, SEARCH_PLACEHOLDER, MEMBER_RESULTS_LABEL, REPORT_ACK, REPORT_NOTE_PROMPT,
  canActOn, canDelete, canEdit, deletionOutcome, visibleBody, postTitle, postComposeViolations, searchEmpty,
  EDITED_MARK,
  nestComments, repliesOpen, repliesToggleLabel, replyComposeState, replyParentFor, visibleReplies,
  commentSendState, commentScrollState, visibleCommentRows,
  // The caps, read from the law rather than typed at the box (R28/R29).
  LIMITS,
  COLLAPSE_ALL_LABEL, isOpen, normalizeOpenSet, openWith, toggleOpen,
  // HER RULING (2026-08-14) — posts render OPEN by default, so the stored set is the CLOSED one and
  // the landing law inverts with it. Same four functions underneath (R28/R29).
  EXPAND_ALL_LABEL, collapseAll, expandWith, postExpanded,
  // COMMENTS COLLAPSE (her `5`) — the second fold's two laws. The SET is carried by the four above
  // it, unchanged, because a set of open ids is one idea and not two (R28/R29).
  commentsFoldLabel, commentsFoldOpen,
  ROOM_READ_FAILED, roomItems, roomNote, SHARE_LABEL,
  // B2 (her order) — the room's own signed-out face, and the ONE law that decides which of the four
  // faces this door wears. This file decides nothing about it: it CALLS the law and renders.
  roomEntryFace, ROOM_INVITE_HEADING, ROOM_INVITE_BODY, ROOM_INVITE_CTA,
  // TRAP-1b — the velvet rope. The FACE is the law's answer and the WORDS are the law's words:
  // this component decides nothing about either (R28/R29).
  VELVET_ROPE_FACE, ropeHeading, VELVET_ROPE_BODY, VELVET_ROPE_ACTION, arrivedFromTag,
  // L2.7 — the ONE answer to what a refused community door does. There is no branch in it that
  // leaves a tap unanswered, which is the property her ruling asks for.
  communityDoorAnswer,
  rangHouseNotice,
  // LR19 — the owner-only held treatment. The LAW, imported: this file decides nothing about who
  // sees a hold.
  heldTreatment,
  // 2026-08-14 — the comment fold's four states, and the sentence a failed read is entitled to.
  // `ROOM_READ_FAILED` is already imported above; this round adds no second spelling of it.
  commentsReadState, COMMENTS_LOADING,
  BLOCK_EVENT,
} from "../livingRoom.js";
import { HeldStrip } from "./HeldStrip";
import { MessageComposer } from "./MessageComposer";
import { fileToBase64, preparePhotoForUpload, videoDuration } from "../photoUpload.js";
import { photoAccept } from "../uploadTypes.js";
import {
  Avatar, ImageViewer, MentionBody, MentionPicker, Who, ago, json, postJson, useMentionPicker,
  type Comment, type Identity, type Me, type Media, type Post,
  LivingRoomMe, OwnerCrown, MemberMenuHost,
} from "./LivingRoomShared";
import { ConfirmDelete, Sheet, ShareSpell, SpellCard } from "./LivingRoomInbox";
import { SharePicker } from "./SharePicker";
import { useRefresh } from "./PullToRefresh";
import { saveDocumentReporting } from "./ManualHandoff";

export function LivingRoomPage({
  onRequireMembership, goHome, isLoggedIn, sessionKnown, onSignIn, canGoLive = false, isOwner = false,
}: {
  // LIVE-1: whether her Go Live button is DRAWN. The server gates the act itself.
  canGoLive?: boolean;
  // LIVE-1: her seat gets the OBS door behind an Advanced link; a member never sees it.
  isOwner?: boolean;
  isMember: boolean;
  // B2 — the two session facts this component never held, and they are two facts (R18/L5.1):
  // *has the session read ANSWERED* and *is she signed in*. Reading the second without the first is
  // the exact shape this room already paid for on a deep link.
  isLoggedIn: boolean;
  sessionKnown: boolean;
  // B2 — **THE SIGN-IN DOOR, WHICH IS NOT `onRequireMembership`.** That one opens the PURPLE BOX,
  // the tier ladder, and a signed-out visitor tapping Join there is answered
  // *"Sign in before choosing a membership."* (ITEM 0 measured that live). This is the house's own
  // sign-in modal.
  onSignIn: () => void;
  // L2.7: the lock is the second argument — without it `requireMembership` runs the action for a
  // member and the refusal is silent.
  onRequireMembership: (action: () => void, lock?: Record<string, unknown>) => void;
  goHome: () => void;
}) {
  const [me, setMe] = useState<Me | null>(null);
  // R83 — **`null` IS "THE ROOM DID NOT ARRIVE", NOT "THE ROOM IS EMPTY"** (W5-2/R39's third state).
  // With `Post[]` starting at `[]` and the read guarded by a bare `if (d?.ok)`, a refused or dropped
  // feed left this at its initial value and the room printed EMPTY_FEED — *"Nothing here yet. Say
  // the first thing."* — over a community that may have been talking all day. R64's Cauldron finding
  // and R70's spellbook finding word for word: not a missing sentence but a WRONG one.
  const [posts, setPosts] = useState<Post[] | null>(null);
  // LR17-4 — her clause (a): a name or @tag surfaces MEMBERS above the posts. Empty on an ordinary
  // feed load, because the server answers `[]` there rather than omitting the field (R18: an absent
  // key and an empty group are two facts, and only one of them is an answer).
  const [members, setMembers] = useState<Identity[]>([]);
  // Which results matched inside a COMMENT — her landing clause, kept as ids rather than as a flag on
  // the row, because the expansion is this component's business (`collapsedPosts` lives here).
  const [commentHits, setCommentHits] = useState<Set<string>>(new Set());
  // The sentence for a READ that did not land. Deliberately not `status`, which belongs to her ACTS:
  // a refusal to post and a feed that never arrived are different facts and must not overwrite each
  // other (R65's note/freshness ruling, one surface down).
  const [readNote, setReadNote] = useState("");
  // B2 — WHICH OF THE FOUR FACES THIS DOOR IS WEARING, decided by `roomEntryFace` and by nothing
  // here. It starts "" because that is what an ok read answers, and `loading` is what holds the
  // screen still until the answer lands.
  const [entryFace, setEntryFace] = useState("");
  const [ropeLock, setRopeLock] = useState<Record<string, unknown> | null>(null);
  // >>> TRAP-1b — READ ONCE, AT THE LANDING, AND NEVER RE-READ. <<< `goTo` rewrites the address,
  // so a re-read at answer time finds the marker gone — which is the funnel's own recorded lesson
  // about `document.referrer` and the utm tag one surface over. A ref, so it survives every
  // render of this room without becoming a reactive value nothing should react to.
  const taggedArrivalRef = useRef(arrivedFromTag(typeof window === "undefined" ? "" : window.location.search));
  const taggedArrival = taggedArrivalRef.current;
  const [section, setSection] = useState<"all" | "spells">("all");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  // LR1-12 ROUND SIX, HER RULING: **COMMENTING HAPPENS INLINE AT THE POST.** No popup, no
  // full-screen modal. The oversized thread modal is gone.
  //
  // LR14, HER RULING: **THE ONE-AT-A-TIME ACCORDION DIES.** This was a single id; it is the SET of
  // ids she has open, it lives on her account, and every other behaviour falls out of it being a
  // set — a new post arrives collapsed because it is not IN it, and a notification tap ADDS to it
  // without touching anything else, which is how LR12-2's landing law composes instead of fighting.
  //
  // **HER RULING, 2026-08-14: POSTS RENDER OPEN BY DEFAULT.** The set is the same shape and means
  // the opposite — these are the ones she has CLOSED — so a member who has closed nothing (which is
  // every member, on the day this ships) finds the whole feed open.
  const [collapsedPosts, setCollapsedPosts] = useState<string[]>([]);
  // The server's answer is the truth, but the tap has to feel instant, so the set moves locally and
  // is then written. `saveCollapsed` sends the WHOLE set (see the route: a delta from a second device
  // would be applied to a room it never saw).
  const saveCollapsed = useCallback(async (next: string[]) => {
    setCollapsedPosts(next);
    const d = await postJson("/api/living-room/open", { collapsed: next });
    // R39: READ THE PAYLOAD. This route answers with what it actually stored.
    if (d?.ok && Array.isArray(d.collapsed)) setCollapsedPosts(d.collapsed);
  }, []);
  // **WHICH POSTS A LANDING HAS EXPANDED ON THIS LOAD, KEPT IN A REF.** It exists for the same
  // reason the old merge did: her stored set arrives a moment after a deep link has already opened
  // its target, and re-applying it would close the very post she was sent to read. A ref rather than
  // state because it must be readable from a callback bound in an earlier render (D-5's ruling).
  const landedOpen = useRef<Set<string>>(new Set());
  // COMMENTS COLLAPSE (her `5`) — **WHICH POSTS ARE SHOWING THEIR COMMENTS, AND IT IS THE SAME
  // SHAPE AS THE SET ABOVE IT.** A set of ids, on her account, through the SAME door — so the two
  // folds cannot be persisted by two disagreeing mechanisms (R28/R29). This write carries ONLY the
  // `comments` key and the one above carries only `open`, which is exactly what lets one route hold
  // both: neither tap can reach the other set (see the route — a key that did not arrive is not an
  // instruction to empty anything).
  const [openComments, setOpenComments] = useState<string[]>([]);
  const saveComments = useCallback(async (next: string[]) => {
    setOpenComments(next);
    const d = await postJson("/api/living-room/open", { comments: next });
    // R39 again, and it is not decoration: the server normalizes and caps, so what came back is
    // what she will find here tomorrow.
    if (d?.ok && Array.isArray(d.comments)) setOpenComments(d.comments);
  }, []);
  // LR12-2: the comment the notification was ABOUT, held until its post's replies have loaded.
  const [wantedComment, setWantedComment] = useState("");
  // LR13: and the REPLY, when the subject was one. The comment id is still carried beside it because
  // it names the thread that has to be EXPANDED before the reply exists on the page to scroll to.
  const [wantedReply, setWantedReply] = useState("");
  // LR14: the post a NOTIFICATION named, which is a different question from which panels are open.
  const [landingPost, setLandingPost] = useState("");
  const [overlay, setOverlay] = useState<"" | "rules" | "spell">("");
  const [q, setQ] = useState("");
  // LR1-12 ROUND THREE: there is no reporting MODAL any more. A flag is one tap; this is the toast.
  const [toast, setToast] = useState<{ text: string; postId?: string; commentId?: string } | null>(null);

  // W1-1: the tier box is raised at the DOOR by App's navigation gate, so reaching this component
  // already means a paid member (L2.7: Newbie and up). This is the belt: a 402 from any call still
  // routes to the box rather than leaving a blank room.
  //
  // **AND THE BELT WAS NOT FASTENED, WHICH IS L2.7's FINDING ONE SURFACE OVER.** It handed
  // `onRequireMembership` an EMPTY action, and that function runs the action for anyone who pays —
  // so this file stated the law in its own comment and did not hold it. `communityDoorAnswer` has no
  // branch that does nothing, and the lock is the server's own.
  // **FREE-1 (2026-08-11) — THE RULING THIS FUNCTION CARRIED IS SUPERSEDED, AND IT IS MIGRATED HERE
  // RATHER THAN OVERRIDDEN BELOW (DL-2/DT-2b/R181).** The mount effect's own comment used to end
  // *"below Apprentice she still meets the wall, capture 08 is that proof"* — and her FREE-1 order
  // is that a member walks into EVERY room with no popup and meets the price at the moment of
  // GENERATION. This room was the only one of the twelve still raising it, and not through a wall:
  // through a FETCH, because the arrival read 402s and every 402 here became the box.
  //
  // So a refusal now carries WHICH MOMENT asked for it. An `arrival` read fills the room, and being
  // refused one means the room opens dressed and empty with `ROOM_BROWSE_ONLY` where the feed would
  // be. An `action` — a post, a comment, a like, a flag — is her clause 2's own moment and raises the
  // box exactly as it always has. **The default is `action`, so a call site that says nothing keeps
  // today's behaviour** and this can only ever be relaxed deliberately, by name.
  const gate = useCallback((d: { locked?: boolean } | null, moment: "arrival" | "action" = "action") => {
    if (!d?.locked) return false;
    if (moment === "arrival") return true;
    const answer = communityDoorAnswer({ isMember: true, lock: d as Record<string, unknown> });
    if (answer.kind === "box") onRequireMembership(() => {}, answer.lock);
    return true;
  }, [onRequireMembership]);

  const loadFeed = useCallback(async (which = section, term = q) => {
    const qs = new URLSearchParams();
    if (which === "spells") qs.set("section", "spells");
    if (term.trim()) qs.set("q", term.trim());
    const d = await json(`/api/living-room/feed${qs.toString() ? `?${qs}` : ""}`);
    // B2 — **THE FACE IS THE LAW'S ANSWER AND THIS FILE COMPOSES NONE OF IT.** One call, four
    // outcomes, and the two that draw no feed are handled here so an invitation and a browse-only
    // room can never both be true (R28/R29: one comparator, one call site).
    const face = roomEntryFace(d, { isLoggedIn, sessionKnown });
    setEntryFace(face);
    // >>> TRAP-1b — THE ROPE CARRIES THE SERVER'S OWN LOCK, AND L2.7 IS WHY. <<< Its law is that
    // nothing hands the gate an empty action **with no lock behind it**, because an empty lock opens
    // the box that knows nothing about the door she touched — the generic "members only" ladder T1
    // exists to have retired, and R213's cold wall one payload over. The rope's own walk captured
    // exactly that: `COME ON IN / Choose your membership` to a member who had just been told what
    // the Living Room is. The payload is the SERVER's, taken where the face is decided, so the two
    // can never disagree (R28/R29: one comparator, one call site) and this file composes none of it.
    setRopeLock(face === VELVET_ROPE_FACE && d && typeof d === "object" ? (d as Record<string, unknown>) : null);
    // FREE-1: the room OPENS for her. An empty list rather than `null`, because `null` is R83's
    // *"the read did not land"* and would leave her looking at a room that is still loading forever.
    // **HER *the invitation is the WHOLE render* CLAUSE RIDES THE SAME BRANCH**, and the leak half is
    // then ARITHMETIC rather than a promise: the server refuses both reads, so there is nothing here
    // to leak and the empties below are what say so.
    // HER BROWSE AMENDMENT (2026-08-19): the browse-only face is retired — a locked member meets
    // the ROPE now, which is its own whole render above. The invitation still lands here.
    if (face === "invite") {
      setPosts([]); setMembers([]); setCommentHits(new Set());
      setReadNote("");
      setLoading(false);
      return;
    }
    // R83: the ANSWER decides, and the guard is above the paint (R52) — a feed that did not land
    // sets `null` and says why in the server's own words, and can never be drawn as an empty room.
    const list = roomItems(d, "posts");
    setPosts(list);
    // LR17-4 — **THE MEMBERS GROUP, AND IT IS READ EXACTLY AS THE POSTS ARE.** `roomItems` is R83's
    // law: a read that did not land is `null` and can never be painted as an empty group. The server
    // sends `[]` on an ordinary feed load, so clearing it is the ANSWER rather than a reset.
    setMembers(roomItems(d, "members") || []);
    // **AND WHICH RESULTS MATCHED IN A COMMENT, WHICH IS HER LANDING CLAUSE.** A post whose only
    // match is inside a comment must open, or she is handed a title with no sign of the words she
    // typed. It is a Set of ids rather than a flag on the row, because the row is `PostCard`'s and
    // the expansion is this component's (`collapsedPosts` already lives here).
    setCommentHits(new Set((list || []).filter((p) => p.matchedComment).map((p) => String(p.id))));
    setReadNote(list ? "" : roomNote(d, ROOM_READ_FAILED));
    setLoading(false);
  }, [isLoggedIn, sessionKnown, section, q]);

  const loadMe = useCallback(async () => {
    const d = await sharedRoomRead("/api/living-room/me", json);
    // FREE-1: her SEAT is refused, so `me` stays null and NO COMPOSER is drawn — which is the honest
    // shape, since posting is exactly what membership opens. The sentence is the feed's, set there.
    // **B2: THE SEAT READ ASKS THE SAME LAW** rather than a second reading of the same refusal
    // (R28/R29) — otherwise this loader, which runs FIRST, writes the server's generic
    // *"Sign in first."* into the note under an invitation that is meant to replace it.
    if (roomEntryFace(d, { isLoggedIn, sessionKnown }) === "invite") return;
    if (gate(d, "arrival")) return;
    // Her seat in the room. A failed read leaves `me` null, which draws NO COMPOSER — so without a
    // sentence she was invited to say the first thing with nothing to say it in.
    if (!d?.ok) { setReadNote((prev) => prev || roomNote(d, ROOM_READ_FAILED)); return; }
    if (d?.ok) setMe(d);
    // LR14: the panels she left open come back with her, from the account rather than the device.
    // Merged rather than assigned, so a deep link that already opened its target on this load is not
    // undone by the stored set arriving a moment later.
    // HER RULING: the stored set is the CLOSED one, so the merge SUBTRACTS what a landing has
    // already expanded rather than adding it — the same clause, inverted with the set it is about.
    if (d?.ok) {
      setCollapsedPosts(() => [...landedOpen.current]
        .reduce((acc, id) => expandWith(acc, id), normalizeOpenSet(d.collapsedPosts)));
    }
    // COMMENTS COLLAPSE: the comment folds she left open come back the same way, merged the same
    // way and for the same reason — a deep link that already opened one on this load is not undone
    // by the stored set arriving a moment later.
    if (d?.ok) setOpenComments((prev) => prev.reduce((acc, id) => openWith(acc, id), normalizeOpenSet(d.openComments)));
  }, [gate, isLoggedIn, sessionKnown]);

  // THE TIER BOX IS RAISED BY THE SERVER'S REFUSAL, NEVER BY A CLIENT GUESS — and this is a fix, not
  // a preference. Raising it from `!isMember` on mount looked right and was wrong on the one path
  // that matters most: a DEEP LINK or a REFRESH on /living-room. On a fresh load the session has not
  // come back yet, so `isMember` is false for a beat, and a paying Apprentice landing on her own
  // bookmark got the membership box thrown in her face before the page had any idea who she was.
  // Measured in the LR1-7 walkthrough, which photographed exactly that.
  //
  // So the door answers instead: `/api/living-room/me` either returns the room or 402s, and `gate()`
  // turns that 402 into the box. Below Apprentice she still meets the wall — capture 08 is that
  // proof — and above it she never sees a flicker. App's navigation gate (W1-1, PAGE_FEATURE) still
  // holds the door shut for an in-app TAP, which is the case it was built for.
  useEffect(() => {
    let live = true;
    (async () => {
      await loadMe();
      if (live) await loadFeed();
    })();
    return () => { live = false; };
  }, [loadMe, loadFeed]);
  // I3: a block from a name's menu takes that member out of the feed at once, by re-reading it (the block is in the SQL).
  useEffect(() => {
    const heard = () => { void loadFeed(); };
    window.addEventListener(BLOCK_EVENT, heard);
    return () => window.removeEventListener(BLOCK_EVENT, heard);
  }, [loadFeed]);

  // ONE TAP, HER RULING. The flag is sent the moment it is pressed; the toast confirms and offers
  // words. No picker, no modal, nothing between her finger and the flag.
  const flag = async (target: { postId?: string; commentId?: string }) => {
    const d = await postJson("/api/living-room/report", target);
    // R83 — **THE SHARPEST OF THIS ROUND, BECAUSE THE RECEIPT WAS THE WHOLE CONTROL.** *"Flagged.
    // This is with Lala now."* was shown for a flag that was refused (400 *"Nothing to flag."*) or
    // that never left the building — R57's journal defect on the one tap a member makes when
    // something in the room has frightened or hurt her. The toast says what really happened, and
    // the offer of words is withheld with it: there is no report for her to add words to.
    const refusal = roomNote(d);
    if (refusal) { setToast({ text: refusal }); return; }
    setToast({ text: REPORT_ACK, ...target });
  };

  // LR1 ROUND FIVE — **A NAME OPENS THE DOOR.** Tapping any @name anywhere in the room opens a
  // conversation with that member, ready to write. The BLOCK LAW is absolute and is enforced where
  // it always is, on the server: a blocked pairing answers "that member is not here" and no composer
  // opens. Tapping your OWN name opens your inbox rather than a conversation with yourself.
  const openConversation = useCallback(async (ref: string) => {
    if (!ref) return;
    if (ref === me?.me.ref) { window.dispatchEvent(new CustomEvent("house-inbox:open")); return; }
    const d = await postJson("/api/living-room/thread", { ref });
    if (d?.ok) window.dispatchEvent(new CustomEvent("house-inbox:open", { detail: { threadId: d.threadId } }));
    else setStatus(d?.message || "");
  }, [me]);

  // P2-7: THE ROOM JOINS THE REFRESH REGISTRY. A pull re-fetches the feed AND the member's own room
  // state (her strike notice, her limits, whether she may still post), because a member who pulled
  // because something felt stale means both.
  useRefresh("living-room", async () => { await Promise.all([loadFeed(), loadMe()]); });

  // LR1-10: a notification can name a post, and the top-menu bell navigates here with it in the URL.
  // LR12-2: it can also name the COMMENT, which is the subject of the sentence she tapped. Expanding
  // the post and stopping there is "the room, unscrolled" one level in — her comment is somewhere
  // below the fold of a card that may hold forty replies, and she is left to hunt for the thing the
  // notification just told her about.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("post");
    // LR14: the landing law COMPOSES, and HER 2026-08-14 RULING INVERTED IT. `expandWith` REMOVES
    // this one from the collapsed set and touches nothing else — on an open-by-default feed, ADDING
    // would close the very post the notification was sent to find, which is the sharpest thing that
    // could have been carried over unchanged. It is deliberately not a toggle, for LR14's own
    // reason: a tap arriving from outside must never be able to close its own target.
    if (wanted) {
      landedOpen.current.add(wanted);
      setCollapsedPosts((prev) => expandWith(prev, wanted));
      setLandingPost(wanted);
    }
    setWantedComment(params.get("comment") || "");
    setWantedReply(params.get("reply") || "");
  }, []);

  // THE POST ITSELF HAS TO COME INTO VIEW, and this is the half her sentence names outright: "the
  // room, unscrolled is a FAILING STATE". Expanding a card that is eleven rows down the feed changes
  // nothing she can see. This runs when the feed arrives rather than on mount, because on mount
  // there is no card to scroll to yet.
  //
  // LR14: it scrolls to the POST THE NOTIFICATION NAMED, not to "the open one" — with a set there is
  // no such thing as the open one, and scrolling to whichever panel she happens to have left open
  // would move the page under a member who arrived by simply walking in.
  useEffect(() => {
    if (!landingPost || !posts?.length) return;
    const card = document.querySelector(`[data-post-id="${CSS.escape(landingPost)}"]`);
    if (card) card.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [landingPost, posts?.length]);


  // B2 — **HER *THE INVITATION IS THE WHOLE RENDER* CLAUSE, READ LITERALLY, AS AN EARLY RETURN.**
  // Everything below this line is a member surface: the section tabs, the search box and the share
  // control can only ever answer 401 for her, which is ITEM A's clause (*if she can see it she can
  // act on it or it does not render*) and A2's dead box. And the leak half is ARITHMETIC rather than
  // a promise — nothing below is reached, so there is no path by which a post, a name or a face
  // could be drawn to signed-out eyes.
  //
  // The Back control survives, because a landing page with no way out of it is the one thing worse
  // than the blank page she reported.
  // ===============================================================================================
  // >>> TRAP-1b — **THE VELVET ROPE.** *DESIRE, THEN EXPLANATION, THEN PRICE. NEVER PRICE FIRST.*
  // ===============================================================================================
  //
  // Her order: a panel in her exact aesthetic that **INTRODUCES the room BEFORE ever asking for
  // money**, with **ONE gold ENTER control**, and THAT tap opens the membership popup — *the rope's
  // upgrade path shows the tiers NORMALLY*.
  //
  // **THE ORDER OF THE THREE THINGS ON THIS PANEL IS THE WHOLE ITEM**, and it is a property of
  // what is drawn rather than of where it sits: the heading is the DESIRE (*you have been
  // tagged*), the body is her own sentence about what the room IS, and the one control is what
  // reaches the price. **Nothing on this panel names a tier, a price or a payment** — the money
  // is what the ENTER tap opens and it cannot arrive before it.
  //
  // It sits ABOVE the signed-out invite because these are different members meeting different
  // faces (R18): the rope's member IS signed in and has an account, and B2's invitation asks a
  // stranger to sign in — offering her that is telling a member the house does not know her.
  if (entryFace === VELVET_ROPE_FACE) {
    return (
      <section className="page-shell lr-page lr-page--rope">
        <button className="back-button" type="button" onClick={goHome}><ArrowLeft size={18} />Back</button>
        <div className="lr-rope" data-velvet-rope>
          <h1 className="lr-rope__head">{ropeHeading(taggedArrival)}</h1>
          <p className="lr-rope__body">{VELVET_ROPE_BODY}</p>
          {/* ONE control, and it opens the tier popup — never a second door beside it. */}
          {/* TWO THINGS RIDE THIS TAP AND ONLY ONE OF THEM IS OBVIOUS.
              THE LOCK is the SERVER's own (L2.7 above): without it the box knows nothing about the
              door she touched and draws the generic ladder, which is the cold wall she is behind the
              rope to be spared. THE ACTION is what happens AFTER she pays — `onRequireMembership`
              runs it for a member who already does — and it must not be empty here: an empty one
              leaves a member who has just paid looking at the rope she paid to get past. Re-reading
              the feed is what puts her IN the room she was invited to, which is her order's own
              destination, and it is a READ rather than a navigation, so it is not a second answer to
              where a paid member lands (R28/R29). */}
          <button type="button" className="lr-rope__cta"
            onClick={() => onRequireMembership(() => { void loadFeed(); }, ropeLock ?? undefined)}>{VELVET_ROPE_ACTION}</button>
        </div>
      </section>
    );
  }

  if (entryFace === "invite") {
    return (
      <section className="page-shell lr-page lr-page--invite">
        <button className="back-button" type="button" onClick={goHome}><ArrowLeft size={18} />Back</button>
        <div className="lr-invite">
          <h1 className="lr-invite__head">{ROOM_INVITE_HEADING}</h1>
          <p className="lr-invite__body">{ROOM_INVITE_BODY}</p>
          <button type="button" className="lr-invite__cta" onClick={onSignIn}>{ROOM_INVITE_CTA}</button>
        </div>
      </section>
    );
  }

  return (
    // ITEM 7 — **THIS IS A MEMBER SURFACE AND THIS IS WHO IS LOOKING.** One line grants the follow
    // tag to every @name this screen draws, because `Who` asks the context rather than a prop at
    // each call site (R27: a capability behind an optional prop lives only where somebody remembered
    // to pass it). Her seat and the notification list say nothing here and get no tag, which is the
    // right answer for both.
    <LivingRoomMe.Provider value={me?.me?.ref ?? null}>
    {/* ITEM 6: mounted ONCE for the whole room — every @name below raises it by event. */}
    <MemberMenuHost onMessage={openConversation} />
    <section className="page-shell lr-page">
      <button className="back-button" type="button" onClick={goHome}><ArrowLeft size={18} />Back</button>

      {/* THE HEADER-PAIR AMENDMENT (her ruling): the bell and the mail icon live in the TOP MENU now,
          between Site Instructions and Member, on every page — see src/components/HouseInbox.tsx.
          The room's local placement is RETIRED and the room keeps only its own content: the feed,
          Shared Spells, the share button and search. */}
      <header className="lr-head">
        <div className="lr-head__title">
          <h1>{ROOM_TITLE}</h1>
        </div>
        <button type="button" className="lr-link" onClick={() => setOverlay("rules")}>House rules</button>
      </header>

      {/* LIVE-1 -- her live circles, at the top of the room where she asked for them. */}
      <LiveRail canGoLive={canGoLive} isOwner={isOwner} />

      {/* >>> **HER BANNER, 2026-09-13: "Greet. Meet. Grow."** <<< Placed exactly where she asked for
          it, centred between the live buttons and the start of the room's own sections, so it is the
          first thing read after the circles and the last thing read before the feed. The words are
          declared in `src/livingRoom.js` with the composer prompt they belong beside, because the
          banner and the box are one instruction to a member and two copies of it would drift. */}
      <div className="lr-banner">
        <p className="lr-banner__title">{ROOM_BANNER}</p>
        <p className="lr-banner__sub">{ROOM_BANNER_SUB}</p>
      </div>

      {/* A STRIKE IS TOLD. Shadow-hide governs content; this governs the account, and an account
          action she is not told about is one she cannot correct. */}
      {me?.silence ? <p className="lr-silenced">{me.silence.notice}</p> : null}

      <nav className="lr-sections" aria-label="Sections">
        {([["all", "The room"], ["spells", SPELLS_SECTION_LABEL]] as const).map(([key, label]) => (
          <button key={key} type="button" className={section === key ? "lr-section is-on" : "lr-section"}
            onClick={() => { setSection(key); setLoading(true); loadFeed(key); }}>{label}</button>
        ))}
        {me?.canPost ? (
          <button type="button" className="lr-share" onClick={() => setOverlay("spell")}>
            <Sparkles size={14} />{SHARE_SPELL_LABEL}
          </button>
        ) : null}
      </nav>

      {/* LR1-12 ROUND TWO — SEARCH THE ROOM: titles, bodies and shared spell names, ordered by the
          SAME freshness expression as the feed, so results are the room filtered rather than a
          second ordering nobody reconciled. */}
      <div className="lr-search">
        <Search size={16} />
        <input
          value={q} placeholder={SEARCH_PLACEHOLDER} aria-label={SEARCH_PLACEHOLDER}
          onChange={(e) => { setQ(e.target.value); setLoading(true); loadFeed(section, e.target.value); }}
        />
        {q ? <button type="button" className="lr-tiny" aria-label="Clear search" onClick={() => { setQ(""); setLoading(true); loadFeed(section, ""); }}><X size={14} /></button> : null}
      </div>

      {me?.canPost && section === "all" ? (
        <Composer me={me} onPosted={() => loadFeed()} setStatus={setStatus} />
      ) : null}
      {status ? <p className="lr-status">{status}</p> : null}

      {loading ? <p className="lr-note">Loading the room...</p> : null}
      {/* R83 — FOUR STATES, NOT TWO (R64's CauldronList): still looking, could not be read, really
          empty, here they are. The read note comes FIRST and SUPERSEDES the invitation, because
          "Nothing here yet. Say the first thing." over a room that never arrived is not a missing
          sentence, it is a wrong one — and the search version is worse still, since it names a term
          she typed as the reason a feed she never received is not on screen. */}
      {!loading && posts === null ? <p className="lr-empty">{readNote || ROOM_READ_FAILED}</p> : null}
      {/* LR17-4 — **MEMBERS FIRST, THEN POSTS**, which is her grouping and not a layout choice: the
          term she typed is one question with two kinds of answer, and burying the people under
          twenty of their own posts makes her scroll past the thing she was looking for. Every row is
          `Who`, so it is room-name identity, the crown, the avatar and the follow tag by EXISTING —
          there is no door to anybody's room or chart here, because `Who` has never had one. */}
      {!loading && members.length ? (
        <section className="lr-results" aria-label={MEMBER_RESULTS_LABEL}>
          <h3 className="lr-results__head">{MEMBER_RESULTS_LABEL}</h3>
          <ul className="lr-results__list">
            {members.map((who) => (
              <li key={who.ref} className="lr-results__row"><Who who={who} size="md" /></li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The empty sentence is asked of BOTH groups, or a term that found three people and no posts
          reads as "Nothing matches" over three matching people (R64: the failure state is not a
          missing sentence but a wrong one).

          **B2 FOUND THAT `ROOM_BROWSE_ONLY` HAD NEVER REACHED A SCREEN, AND IT IS THAT SAME RULING
          ONE FACE OVER.** FREE-1 fills this room for a below-tier member with `posts: []` and the
          browse-only sentence in `readNote` — and `readNote` is drawn only where `posts === null`,
          so what she really met was `EMPTY_FEED`: **"Nothing here yet. Say the first thing." over a
          room holding fifty posts, inviting her to post in the one room she cannot post in.** Found
          by B2's own pairing walk, because a source fixture cannot see which of two sentences
          painted. `entryFace` is what makes the answer exact rather than inferred from an empty
          list. */}
      {!loading && posts !== null && !posts.length && !members.length ? (
        <p className="lr-empty">{q.trim() ? searchEmpty(q) : section === "spells" ? EMPTY_SPELLS : EMPTY_FEED}</p>
      ) : null}

      {/* LR14 — THE CONVENIENCE, for the member drowning in open panels. It is offered only when
          there is something on screen to collapse, because a control that does nothing is a control
          she has to learn to ignore. */}
      {/* HER RULING (2026-08-14) MADE THIS CONTROL THE WORK RATHER THAN THE UNDO, so it carries BOTH
          labels: on an open-by-default feed the convenience she needs is the one that clears the
          screen, and after a collapse-all there has to be a way back that is not forty taps. It is
          still offered only when there is something on screen to act on. */}
      {(posts || []).length ? (
        <button
          type="button" className="lr-collapse-all"
          onClick={() => saveCollapsed(
            (posts || []).some((p) => postExpanded(collapsedPosts, p.id))
              ? collapseAll(collapsedPosts, (posts || []).map((p) => p.id))
              : [],
          )}
        >
          {(posts || []).some((p) => postExpanded(collapsedPosts, p.id)) ? COLLAPSE_ALL_LABEL : EXPAND_ALL_LABEL}
        </button>
      ) : null}

      <div className="lr-feed">
        {(posts || []).map((p) => (
          <PostCard
            key={p.id} post={p} me={me}
            // LR17-4 — **A RESULT THAT MATCHED IN A COMMENT LANDS EXPANDED.** Her case is exactly
            // this: *"money spell" must find the post even when the words live in a COMMENT, landing
            // expanded per the landing law.* Collapsed, the row shows a title that does not contain
            // the term and the member is left wondering why the room handed it to her. It is an OR
            // rather than an override, so a post she has deliberately collapsed is not forced open on
            // an ordinary feed load — the set is empty unless she is searching.
            expanded={postExpanded(collapsedPosts, p.id) || commentHits.has(String(p.id))}
            highlightComment={landingPost === p.id ? wantedComment : ""}
            highlightReply={landingPost === p.id ? wantedReply : ""}
            // COMMENTS COLLAPSE (her `5`) — the SECOND fold, and its three landing reasons are
            // handed in rather than guessed at inside the card: her stored set, a search that
            // matched inside a COMMENT, and a notification or shared link naming a comment or a
            // reply. `commentsFoldOpen` is the law that weighs them, so a gate RUNS it.
            commentsOpen={isOpen(openComments, p.id)}
            commentsMatched={commentHits.has(String(p.id))}
            onToggleComments={() => saveComments(toggleOpen(openComments, p.id))}
            onOpen={() => saveCollapsed(toggleOpen(collapsedPosts, p.id))}
            onChanged={() => loadFeed()}
            onLike={async () => {
              const d = await postJson("/api/living-room/like", { postId: p.id });
              // R83 DECLARED AND NOT CHANGED: the heart is NOT painted ahead of its answer, so a
              // refused like leaves the control exactly as it was and claims nothing. That is the
              // one shape on this surface that is honest by construction rather than by accident.
              if (d?.ok) setPosts((prev) => (prev || []).map((x) => (x.id === p.id ? { ...x, liked: d.liked, likes: d.likes } : x)));
            }}
            onReport={(ids) => flag(ids || { postId: p.id })}
            onOpenMember={openConversation}
          />
        ))}
      </div>

      {overlay === "rules" && me ? <RulesSheet guidelines={me.guidelines} onClose={() => setOverlay("")} /> : null}
      {overlay === "spell" ? <ShareSpell onClose={() => setOverlay("")} onShared={() => { setSection("spells"); loadFeed("spells"); }} /> : null}
      {toast ? <FlagToast toast={toast} onClose={() => setToast(null)} /> : null}
    </section>
    </LivingRoomMe.Provider>
  );
}

// ---------------------------------------------------------------------------
// THE COMPOSER. Text, pictures, video, @tags — and the limits STATED, which LR1-2 asked for by name.
// ---------------------------------------------------------------------------
function Composer({ me, onPosted, setStatus }: { me: Me; onPosted: () => void; setStatus: (s: string) => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [media, setMedia] = useState<Array<{ kind: "image" | "video" | "document"; data: string; contentType: string; durationSeconds?: number; preview: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const documentInput = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const picker = useMentionPicker(null, Boolean(me?.me?.owner));

  const addImages = async (files: FileList | null) => {
    if (!files?.length) return;
    const next: typeof media = [];
    for (const file of Array.from(files).slice(0, me.limits.images)) {
      // The SHARED resize path (LR1-2 named it): the same `preparePhotoForUpload` the palm and tarot
      // uploads use, so a 12MP phone photo never leaves the device at full size.
      const prepared = await preparePhotoForUpload(file).catch(() => null);
      if (!prepared) { setStatus("That picture would not open."); continue; }
      next.push({
        kind: "image", data: prepared.dataUrl.split(",")[1] || "",
        contentType: prepared.contentType || "image/jpeg",
        preview: prepared.dataUrl, name: file.name,
      });
    }
    setMedia((prev) => [...prev.filter((m) => m.kind === "image"), ...next].slice(0, me.limits.images));
  };

  const addVideo = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    // Checked HERE as well as on the server, so she is told before a 50MB upload runs.
    if (file.size > me.limits.videoBytes) { setStatus(me.limitCopy.video); return; }
    const duration = await videoDuration(file).catch(() => 0);
    if (duration && duration > me.limits.videoSeconds) { setStatus(me.limitCopy.video); return; }
    setMedia([{
      kind: "video", data: await fileToBase64(file), contentType: file.type || "video/mp4",
      durationSeconds: Math.round(duration), preview: "", name: file.name,
    }]);
  };

  // HER ORDER 2026-09-30 — A PDF OR A BOOK, UP TO 25MB. Asked of the SAME tray law the server runs
  // (`postDocumentRefusal`, sniffing the file's own first bytes), so she is told before a 25MB encode
  // runs, in the words the server would answer with. A document travels alone, so it replaces the tray.
  const addDocument = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const head = new Uint8Array(await file.slice(0, 64).arrayBuffer().catch(() => new ArrayBuffer(0)));
    const refusal = postDocumentRefusal([{ kind: "document", bytes: file.size, head }]) as string;
    if (refusal) { setStatus(refusal); return; }
    setMedia([{
      kind: "document", data: await fileToBase64(file), contentType: file.type || "application/pdf",
      preview: "", name: file.name,
    }]);
  };

  const submit = async () => {
    if (busy) return;
    // LR1-12 ROUND TWO: the title is REQUIRED, checked here from the SAME law the server enforces,
    // so she is told before the request rather than after it.
    const shape = postComposeViolations({ title, body, media: media.length }) as string[];
    if (shape.length) { setStatus(shape[0]); return; }
    setBusy(true);
    setStatus("");
    const d = await postJson("/api/living-room/post", {
      title,
      body,
      // A document's own name rides so its card and its download keep her title; pictures and clips
      // send exactly what they always sent.
      media: media.map((m) => ({ kind: m.kind, data: m.data, contentType: m.contentType, durationSeconds: m.durationSeconds, ...(m.kind === "document" ? { name: m.name } : {}) })),
    });
    setBusy(false);
    if (!d?.ok) { setStatus(d?.message || "That did not send."); return; }
    // A HELD POST LANDS HERE TOO, and takes the identical path: the box clears, the feed reloads, her
    // post is at the top. She is told nothing, because there is nothing in `d` that says anything.
    // HER CONFIRMATION: the author is excluded from her own fan-out, so without this she rings the
    // whole house and her screen says nothing at all.
    setStatus(rangHouseNotice(d.rangHouse));
    setTitle("");
    setBody("");
    setMedia([]);
    onPosted();
  };

  return (
    <div className="lr-composer">
      <input
        className="lr-composer__title" value={title} maxLength={me.limits.titleChars}
        placeholder="Give it a title" aria-label="Title"
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="lr-composer__field">
        <textarea
          ref={boxRef} className="lr-composer__box" value={body} rows={3}
          maxLength={me.limits.postChars} placeholder={COMPOSER_PLACEHOLDER}
          onChange={(e) => { setBody(e.target.value); picker.look(e.target.value, e.target.selectionStart || 0); }}
          onBlur={picker.dismiss}
        />
        <MentionPicker anchor={boxRef} people={picker.people} onPick={(name) => {
          const next = picker.choose(body, name);
          setBody(next.text);
          requestAnimationFrame(() => boxRef.current?.setSelectionRange(next.caret, next.caret));
        }} />
      </div>
      {media.length ? (
        <div className="lr-composer__media">
          {media.map((m, i) => (
            <span key={i} className="lr-chip">
              {m.kind === "image" ? <img src={m.preview} alt="" /> : m.kind === "document" ? <FileText size={14} /> : <Video size={14} />}
              <span className="lr-chip__name">{m.name}</span>
              <button type="button" aria-label="Remove" onClick={() => setMedia((prev) => prev.filter((_, x) => x !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="lr-composer__bar">
        <div className="lr-composer__tools">
          <button type="button" className="lr-iconbtn" aria-label="Add pictures" onClick={() => imageInput.current?.click()}><ImagePlus size={18} /></button>
          <button type="button" className="lr-iconbtn" aria-label="Add a video" onClick={() => videoInput.current?.click()}><Video size={18} /></button>
          <button type="button" className="lr-iconbtn" aria-label="Add a PDF or book" onClick={() => documentInput.current?.click()}><FileText size={18} /></button>
          <input ref={imageInput} type="file" accept={photoAccept()} multiple hidden onChange={(e) => addImages(e.target.files)} />
          <input ref={videoInput} type="file" accept="video/*" hidden onChange={(e) => addVideo(e.target.files)} />
          {/* The picker offers what the server's roster takes, by type and by extension (a phone often
              hands over an EPUB with no type at all). */}
          <input ref={documentInput} type="file" accept={postDocumentAccept()} hidden onChange={(e) => { addDocument(e.target.files); e.target.value = ""; }} />
        </div>
        <button type="button" className="button" disabled={busy || !title.trim() || (!body.trim() && !media.length)} onClick={submit}>
          {busy ? "Sending..." : "Post"}
        </button>
      </div>
      {/* THE HONEST LIMITS, IN-UI. Composed on the server from the same numbers it enforces. */}
      <p className="lr-limits">{me.limitCopy.title} {me.limitCopy.post} {me.limitCopy.images} {me.limitCopy.video} {me.limitCopy.document}</p>
    </div>
  );
}

// LR16: `fileToBase64` and `videoDuration` moved to `src/photoUpload.js`, the module that already
// owns "prepare a file for upload", so the inbox composer reaches the SAME two rather than re-typing
// them (R28/R29). Nothing about either changed.

// ---------------------------------------------------------------------------
// LR19 — **THE ONE PLACE THIS ROOM TALKS TO HER MODERATION DOOR.** The strip itself is hookless and
// lives in its own file so a gate can call it; this wrapper is the state it needs (which row is
// open, which button is in flight) and the request.
//
// **IT POSTS TO THE SAME ROUTE AND THE SAME ACTION KEYS HER DASHBOARD USES** — `/api/owner/living-
// room/action`, which is owner-gated server-side by `ownerOnly` and composes its own sentence for
// the append-only ledger (the D2 law). A second endpoint for "approve from the feed" would be a
// second answer to what approving IS, and this house has paid for that shape more than once. The
// route already refuses everybody else, so a member who forged this call gets a 403 rather than a
// render decision (the yellow is the SECOND wall; the route is the first).
//
// **D-5's IN-FLIGHT GUARD, ONE ROOM OVER AND FOR THE SAME REASON.** `busy` is a ref-free single
// string here because a second tap must not fire a second moderation act — a double-tapped Approve
// on a slow phone would write two ledger rows and two overturn rows for one decision, which is the
// teaching arithmetic (`overturnPressure`) counting a class twice off one press.
function HeldPanel({
  item, itemKind, me, onChanged,
}: {
  item: Post | Comment; itemKind: "post" | "comment"; me: Me | null; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const inFlight = useRef(false);
  const treatment = heldTreatment(item, { owner: me?.me?.owner === true });
  if (!treatment) return null;
  const act = async (action: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(action);
    try {
      const d = await postJson("/api/owner/living-room/action", {
        action, itemKind, id: item.id,
        // The sentence her ledger records is composed by the SERVER; this is only the name that
        // goes into it, and it is the room first-name the room already renders (never an email).
        authorName: item.author?.name || "a member",
      });
      if (d?.ok) { setOpen(false); onChanged(); }
    } finally {
      inFlight.current = false;
      setBusy("");
    }
  };
  return (
    <HeldStrip
      treatment={treatment} open={open} busy={busy}
      onToggle={() => setOpen((v) => !v)} onAct={act}
    />
  );
}

// A POST.
// LR1-12 ROUND EIGHT — **THE FEED IS TITLE ROWS. THE BIG ALWAYS-OPEN PANELS ARE GONE.**
//
// Her ruling, restated because round one's version under-delivered: the feed is compact lines
// flowing down the page — **the author's FACE**, title, @name, time, like and reply counts. Tapping a
// title EXPANDS IT IN PLACE, an accordion: the full post unfolds right there with its comments and
// its compose box beneath, and tapping it again (or another row) collapses it. Shared spells follow
// the same pattern: a title row that opens into the full spell card.
//
// **AND RULING 3 IS THE SAME COMPONENT'S JOB:** when it is open, the whole thing is ONE card. The
// body, the media, the actions and the comments are all inside a single bordered panel with no gap
// between them, so nothing reads as an orphaned block drifting over her background art.
function PostCard({
  post: p, me, onOpen, onLike, onReport, onChanged, onOpenMember, expanded = false,
  highlightComment = "", highlightReply = "",
  commentsOpen = false, commentsMatched = false, onToggleComments,
}: {
  post: Post; me: Me | null; onOpen: () => void; onLike: () => void;
  onReport: (ids?: { postId?: string; commentId?: string }) => void;
  onChanged: () => void; onOpenMember?: (ref: string) => void; expanded?: boolean;
  highlightComment?: string; highlightReply?: string;
  commentsOpen?: boolean; commentsMatched?: boolean; onToggleComments?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // ITEM 7: the share picker is per-post state — it opens over the card she tapped.
  const [sharing, setSharing] = useState(false);
  // ITEM 4(b): the post's OWN pictures open in the same viewer a comment's do. Measured before
  // building it: this room had no full-size view at all, so a member could not read the label on a
  // bottle in somebody's altar photo. Building it only for comments would have put the capability on
  // whichever surface needed it first, which is the shape W12, R26 and R35 all paid for.
  const [viewing, setViewing] = useState(-1);
  const isSpell = p.kind === SPELL_KIND && p.spell;
  // LR19 — **THE YELLOW HAS TO REACH THE COLLAPSED ROW, BECAUSE THE COLLAPSED ROW IS THE FEED.** Her
  // words are *"unmistakable at a glance"*, and at a glance this room is a list of title lines: a
  // treatment that only appeared after she opened a post would be discoverable exactly the way the
  // dashboard already was. The same law decides both — one function, asked twice, never two rules.
  const held = heldTreatment(p, { owner: me?.me?.owner === true });
  // COMMENTS COLLAPSE (her `5`) — the fold's two answers, both from the law rather than from a
  // ternary here, so a gate can RUN them. `highlightComment || highlightReply` is the notification
  // and shared-link landing; `commentsMatched` is the room's search having matched inside a comment.
  // Both OPEN the fold and neither touches her stored set, so a landing can never close anything.
  const commentsShown = commentsFoldOpen({
    open: commentsOpen, matched: commentsMatched, wanted: highlightReply || highlightComment,
  });
  const fold = commentsFoldLabel(p.comments || 0, commentsShown);

  return (
    <article
      className={`lr-post${p.pinned ? " is-pinned" : ""}${expanded ? " is-open" : ""}${held ? " is-held" : ""}`}
      data-post-id={p.id} data-held={held ? held.state : undefined}
    >
      {/* THE ROW. The whole of a collapsed post: one tappable line. **LR17-5 ADDED THE FACE TO IT**
          — *"the feed shows faces closed or open"* — which supersedes LR1-12 round eight's
          "nothing else"; that note is corrected above rather than left standing (R68). */}
      <button
        type="button" className="lr-row" onClick={onOpen}
        aria-expanded={expanded} aria-label={`${postTitle(p)} — open`}
      >
        {/* LR17-5, HER RULING: **THE FEED SHOWS FACES CLOSED OR OPEN.** *"The author's profile
            picture on every collapsed title row, same size as expanded, feed AND Shared Spells,
            house avatar where no photo, crowns where held, mobile and desktop."*

            **THIS SUPERSEDES LR1-12 ROUND EIGHT**, which cut this row to *"title, @name, time,
            counts and NOTHING else"* — and her word is the later one. It is the SHARED `Avatar`
            rather than an `<img>` written here, so the house glyph where there is no photo, the
            owner ring and the 404 fallback all arrive by EXISTING (ITEM 4c's whole argument: one
            component draws a member, so a fact carried there reaches every surface).

            `size="sm"` is the expanded head's own size, read from the `<Who>` below rather than
            chosen here — her ruling is *same size as expanded*, so the two must not be able to
            drift apart. */}
        <Avatar who={p.author} size="sm" />
        <span className="lr-row__main">
          {p.pinned ? <Pin size={11} className="lr-row__pin" /> : null}
          <span className="lr-row__title">{postTitle(p)}</span>
          <span className="lr-row__meta">
            <span className="lr-row__who">@{p.author.name}<OwnerCrown who={p.author} /></span>
            <span className="lr-when">{ago(p.createdAt)}</span>
            {p.editedAt ? <span className="lr-edited">{EDITED_MARK}</span> : null}
          </span>
        </span>
        <span className="lr-row__counts">
          <span><ThumbsUp size={13} />{p.likes || 0}</span>
          <span><MessageCircle size={13} />{p.comments || 0}</span>
          <ChevronDown size={15} className="lr-row__chev" />
        </span>
      </button>

      {/* THE UNFOLD. Everything below exists only while this row is open. */}
      {expanded ? (
        <div className="lr-open">
          {/* LR19 — HER ACTIONS, AT THE TOP OF THE THING SHE IS JUDGING. Above the byline on purpose:
              the first thing on an opened held post is the fact that it is held, and the two words
              that settle it. Draws nothing at all for a member (`heldTreatment` returns null). */}
          <HeldPanel item={p} itemKind="post" me={me} onChanged={onChanged} />
          <div className="lr-open__head">
            <Who who={p.author} at={p.createdAt} edited={Boolean(p.editedAt)} size="sm"
              onOpen={onOpenMember ? () => onOpenMember(p.authorRef) : undefined} />
            <span className="lr-post__own">
              {canEdit(p, { ref: me?.me.ref }) && !isSpell ? (
                <button type="button" className="lr-tiny" onClick={() => setEditing(true)} aria-label="Edit"><Pencil size={13} /></button>
              ) : null}
              {canDelete(p, { ref: me?.me.ref, owner: me?.me.owner }) ? (
                <button type="button" className="lr-tiny" onClick={() => setConfirming(true)} aria-label="Delete"><Trash2 size={13} /></button>
              ) : null}
            </span>
          </div>

          {isSpell ? (
            <SpellCard spell={p.spell!} author={p.author} actions={me?.spellActions || []} onUsed={() => onChanged()} />
          ) : (
            // `visibleBody` is the two-body law: the author sees her pending draft, everyone else
            // sees the last version that passed a gate.
            <MentionBody text={visibleBody(p, { ref: me?.me.ref })} mentions={p.mentions} className="lr-post__body" />
          )}

          {/* HER ORDER 2026-09-30 — A PDF OR BOOK IS A NAMED CARD, never embedded: her title, what it is
              and its size, then Open (a PDF reads in the browser) and Download (both). The bytes ride
              the room's one gated attachment route, so shadow-hide, blocks and deletes cover the file
              exactly as they cover a picture. A document travels alone, so it replaces the strip. */}
          {p.media?.length && p.media[0].kind === "document" ? (
            <div className="lr-doc">
              <FileText size={22} className="lr-doc__icon" aria-hidden="true" />
              <div className="lr-doc__text">
                <span className="lr-doc__name">{p.media[0].fileName || "Document"}</span>
                <span className="lr-doc__meta">{documentCardLabel({ contentType: p.media[0].contentType, sizeBytes: p.media[0].sizeBytes })}</span>
              </div>
              <div className="lr-doc__actions">
                {documentOpensInPlace(p.media[0].contentType) ? (
                  <a className="lr-doc__btn" href={p.media[0].url} target="_blank" rel="noopener noreferrer"
                    aria-label={`Open ${p.media[0].fileName || "this document"}`}>Open</a>
                ) : null}
                {/* IT #391 (2026-10-11): a bare `<a download>` does nothing in the iPhone home-screen app, so a book posted here
                    could be read and never kept. The house's one transport (#127/R101) fetches it and saves it under her title. */}
                <button type="button" className="lr-doc__btn" onClick={() => { const url = p.media?.[0]?.url; if (url) void saveDocumentReporting(url); }}
                  aria-label={`Download ${p.media[0].fileName || "this document"}`}>Download</button>
              </div>
            </div>
          ) : p.media?.length ? (
            <div className={`lr-media lr-media--${Math.min(p.media.length, 4)}`}>
              {p.media.map((m, i) => (m.kind === "video"
                ? <video key={m.id} src={m.url} controls playsInline preload="metadata" />
                : (
                  <button key={m.id} type="button" className="lr-media__open" aria-label="See this picture full size"
                    onClick={() => setViewing(i)}>
                    <img src={m.url} alt="" loading="lazy" />
                  </button>
                )))}
            </div>
          ) : null}
          {viewing >= 0 && p.media?.length ? (
            /* ITEM 7 — **THE IMAGES-ONLY FILTER IS DELETED, NOT ADJUSTED, AND IT WAS ALSO A LIVE
               INDEX BUG.** The viewer draws a `<video>` now, so a filtered list is no longer needed
               to keep one out of it — and the index this call site passes has always been the index
               within the WHOLE strip (`p.media.map((m, i) …)`) while the list it was paired with was
               FILTERED, so a post whose first item is a video opened the wrong photograph. Handing
               over the whole strip makes the two agree by construction (R28/R29). */
            <ImageViewer items={p.media} index={viewing} onClose={() => setViewing(-1)} />
          ) : null}

          <footer className="lr-post__foot">
            <button type="button" className={p.liked ? "lr-act is-on" : "lr-act"} onClick={onLike} aria-pressed={p.liked} aria-label="Thumbs up">
              <ThumbsUp size={16} />{p.likes || ""}
            </button>
            {/* QC-9 — **THE MESSAGE ICON IS GONE FROM THIS ROW, HER RULING.** It opened the inbox
                and nothing more, which the name menu on the author's own @name already does — two
                controls for one door, and the row is where she reads a post rather than where she
                navigates. The capability did not move: tapping a name still offers Message. */}
            {/* ITEM 7 — SHARE. On every post and, because a shared spell IS a post in this room, on
                every shared spell by construction rather than by a second control. **QC-9 made it
                the WORD**: an icon-only share is a control she could not find on the live site, and
                that is the whole report. The label is the shared law, never a literal here. */}
            <button type="button" className="lr-act lr-act--word" onClick={() => setSharing(true)}
              aria-label={`${SHARE_LABEL} this post`}><Share2 size={14} />{SHARE_LABEL}</button>
            <button type="button" className="lr-act lr-act--quiet" onClick={() => onReport({ postId: p.id })} aria-label="Report this post"><Flag size={14} /></button>
          </footer>
          {sharing ? <SharePicker postId={p.id} onClose={() => setSharing(false)} /> : null}

          {/* COMMENTS COLLAPSE (her `5`) — **THE SECOND FOLD, AND ITS LINE IS THE ONLY DOOR TO THE
              COMPOSE BOX.** Her clause is *composer only when open*, so this control is drawn
              UNCONDITIONALLY: on a post with no comments at all it is what says "Add a comment",
              because a line hidden on zero would mean nobody could ever write the first one.

              **THE COUNT IS THE ROW'S OWN NUMBER.** `p.comments` is what the collapsed line above
              already renders, so the fold and the feed can never disagree about how many comments a
              post has — and it needs no fetch, which is what keeps a shut fold free. */}
          <button
            type="button"
            className={`lr-comments-fold${fold.kind === "first" ? " lr-comments-fold--first" : ""}`}
            aria-expanded={commentsShown} onClick={onToggleComments}
          >
            <MessageCircle size={14} />
            <span>{fold.text}</span>
            <ChevronDown size={14} className="lr-comments-fold__chev" />
          </button>

          {/* RULING 3: the replies are INSIDE this card, attached to the body above them.
              **AND THEY ARE NOT RENDERED AT ALL WHILE THE FOLD IS SHUT** rather than hidden with
              CSS — R27's own law, the same one the post fold above obeys. See DECISIONS
              COMMENTS-COLLAPSE-1: a hidden-but-mounted list still FETCHES the whole thread for
              every open post nobody asked to read, and `scrollIntoView` cannot land on a row inside
              a `display: none` box, which is precisely what her three landing paths need. */}
          {commentsShown ? (
            <InlineComments
              postId={p.id} post={p} me={me} onChanged={onChanged}
              onReport={(ids) => onReport(ids)} onOpenMember={onOpenMember}
              highlight={highlightComment} highlightReply={highlightReply}
            />
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <EditSheet
          itemKind="post" id={p.id} initial={visibleBody(p, { ref: me?.me.ref })}
          initialTitle={postTitle(p)}
          onClose={() => setEditing(false)} onDone={() => { setEditing(false); onChanged(); }}
        />
      ) : null}
      {confirming ? (
        <ConfirmDelete
          itemKind="post" id={p.id} isSpell={Boolean(isSpell)}
          onClose={() => setConfirming(false)} onDone={() => { setConfirming(false); onChanged(); }}
        />
      ) : null}
    </article>
  );
}

// ---------------------------------------------------------------------------
// LR1-16 — EDIT. The author is never told her edit failed, which is the shadow-hide law applied to
// an edit; the response is identical either way and there is nothing here to render a failure with.
// ---------------------------------------------------------------------------
function EditSheet({
  itemKind, id, initial, initialTitle, onClose, onDone,
}: { itemKind: "post" | "comment" | "message"; id: string; initial: string; initialTitle?: string; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState(initial);
  // An old post arrives here wearing its borrowed first line as a title. Editing is exactly where she
  // gives it a real one, which is why the fallback lives in the read path and not in a backfill.
  const [title, setTitle] = useState(initialTitle || "");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  return (
    <Sheet label="Edit" onClose={onClose} className="lr-modal--edit">
      <h2>Edit</h2>
      {itemKind === "post" ? (
        <input className="lr-input" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" placeholder="Title" />
      ) : null}
      <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} aria-label="Edit your words" />
      {status ? <p className="lr-status">{status}</p> : null}
      <div className="lr-confirm">
        <button type="button" className="button button--ghost" onClick={onClose}>Never mind</button>
        <button type="button" className="button" disabled={busy} onClick={async () => {
          setBusy(true);
          const d = await postJson("/api/living-room/edit", { itemKind, id, body: text, ...(itemKind === "post" ? { title } : {}) });
          setBusy(false);
          if (!d?.ok) { setStatus(d?.message || "That did not save."); return; }
          onDone();
        }}>{busy ? "Saving..." : "Save"}</button>
      </div>
    </Sheet>
  );
}

// LR1-16 — DELETE. **`ConfirmDelete` MOVED TO `LivingRoomInbox.tsx` IN LR16-e**, beside `Sheet`,
// which this file already imports from there — so the ONE confirm this room asks before it destroys
// anything is reachable by the inbox as well, on its third kind. It was never copied: a second
// confirm is a second answer to what this house says before it takes something of hers away.
// ---------------------------------------------------------------------------
// LR1-12 ROUND SIX — **COMMENTS INLINE, AT THE POST.** Her ruling, and the oversized modal is gone.
//
// Tapping the comment action expands this directly beneath the post it belongs to, in the feed or in
// the Shared Spells section, and submitting adds the reply IN PLACE with no navigation. Nothing here
// is fixed-position, nothing portals, nothing takes over the screen: it is a block in the card.
//
// The @-picker floats correctly above it because `.lr-reply__field` carries its own stacking context
// (round two's fix), and the compose box is keyboard-safe on iOS for the same reason every other
// in-page input on this site is — it scrolls with the page rather than being pinned to the viewport,
// which is what the standing iOS work settled.
// ---------------------------------------------------------------------------
//
// LR13 — AND THE REPLIES UNDER THEM, ONE LEVEL DEEP. The nesting, the collapse and the pre-tag are
// laws in `src/livingRoom.js` (`nestComments`, `visibleReplies`, `repliesToggleLabel`,
// `replyComposeState`, `replyParentFor`), so a gate runs every one of them without a browser and
// this component only places what they decide.
function InlineComments({
  postId, post, me, onChanged, onReport, onOpenMember, highlight = "", highlightReply = "",
}: {
  postId: string; post: Post; me: Me | null; onChanged: () => void;
  onReport: (ids: { postId?: string; commentId?: string }) => void;
  onOpenMember?: (ref: string) => void; highlight?: string; highlightReply?: string;
}) {
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState<Comment | null>(null);
  const [deleting, setDeleting] = useState<Comment | null>(null);
  // WHICH COMMENT THE BOX IS ANSWERING. Null is the post itself, which is the box that has always
  // been here. Set, and the compose moves under that thread with the tag already typed.
  const [answering, setAnswering] = useState<Comment | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // D-5: the two halves of one guard — the ref DECIDES (synchronously, inside a single frame) and
  // the state is only what the button wears. See `send` below for why one of them is not enough.
  const inFlight = useRef(false);
  const [sending, setSending] = useState(false);
  // ITEM 4(b): pictures waiting to go with this comment, and the one open full-size view.
  const [shots, setShots] = useState<Array<{ data: string; contentType: string; preview: string; name: string }>>([]);
  const [viewing, setViewing] = useState<{ items: Media[]; index: number } | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const picker = useMentionPicker(null, Boolean(me?.me?.owner));

  // 2026-08-14 — **A 500 AND A SLOW ANSWER WERE ONE STATE ON HER SCREEN.** This read set `comments`
  // to null on a failure and never cleared it, so the fold rendered *"Loading…"* forever; and the
  // sentence beside it could be the empty string, because a thrown request answers `null` and R72's
  // 500 need carry no message this side reads. `commentsReadState` is the four states (R18/R64), and
  // `readFailed` is what tells *the answer has not come* from *the answer came and was a refusal* —
  // two facts, and only one of them is a wait.
  const [readFailed, setReadFailed] = useState(false);
  const load = useCallback(async () => {
    const d = await json(`/api/living-room/post/${postId}`);
    const read = commentsReadState(d);
    if (read.state === "failed") { setReadFailed(true); setStatus(read.note); return; }
    setReadFailed(false);
    setComments(read.items || []);
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  // LR12-2 — HER COMMENT, FOUND FOR HER. This waits on `comments` rather than running with the
  // deep link, because the id in the URL means nothing until the replies it names are on the page.
  // The mark is BRIEF and it is a class, not a scroll position: a highlight that never fades reads
  // as a state the comment is in, and a member who came here by tapping a bell already knows which
  // one she is looking for the moment her eye lands on it.
  const [found, setFound] = useState("");
  useEffect(() => {
    // LR13: the REPLY is the subject when there is one — the comment id beside it names the thread,
    // which `repliesOpen` has already expanded by the time this runs, so the row exists to scroll to.
    const wanted = highlightReply || highlight;
    if (!wanted || !comments) return;
    if (!comments.some((c) => c.id === wanted)) return;
    const el = document.querySelector(`[data-comment-id="${CSS.escape(wanted)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setFound(wanted);
    const t = window.setTimeout(() => setFound(""), 2600);
    return () => window.clearTimeout(t);
  }, [highlight, highlightReply, comments]);

  // ITEM 4(b): THE SAME `preparePhotoForUpload` the composer, the palm room and the tarot upload
  // use, so a 12MP phone photo never leaves the device at full size — one resize path on this site.
  const addShots = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = (me?.limits.images || 4) - shots.length;
    const next: typeof shots = [];
    for (const file of Array.from(files).slice(0, Math.max(0, room))) {
      const prepared = await preparePhotoForUpload(file).catch(() => null);
      if (!prepared) { setStatus("That picture would not open."); continue; }
      next.push({
        data: prepared.dataUrl.split(",")[1] || "", contentType: prepared.contentType || "image/jpeg",
        preview: prepared.dataUrl, name: file.name,
      });
    }
    setShots((prev) => [...prev, ...next].slice(0, me?.limits.images || 4));
  };

  const send = async () => {
    // D-5 — **THE IN-FLIGHT GUARD, AND IT IS A REF AS WELL AS STATE ON PURPOSE.** `sending` is what
    // makes the button LOOK busy; it cannot be what DECIDES, because a state read inside this
    // closure is the value from the render that bound the handler — so three Enter presses inside
    // one frame all see `false` and all three send. That is not a hypothetical: key auto-repeat and
    // a double tap on a slow phone both land inside one frame, and the room's three live duplicate
    // replies are byte-identical, one parent, one member. **The ref is synchronous and needs no
    // re-render, which is the only thing that closes the window this defect lives in.**
    // The decision itself is `src/livingRoom.js`'s, so a gate RUNS it rather than reading this line.
    if (commentSendState({ inFlight: inFlight.current, text: body, pictures: shots.length }) !== "send") return;
    // ONE LEVEL: `replyParentFor` collapses a reply-to-a-reply into its thread root, here and on the
    // server, from the one law both import.
    const parentId = answering ? replyParentFor(answering) : null;
    inFlight.current = true;
    setSending(true);
    // S1b — **THE OPTIMISTIC ROW**, her ruling for the comment surface, and it is S1's DM pattern
    // rather than a second one (R28/R29): the pending row is LOCAL, the server's own answer replaces
    // the whole list on confirm, so there is no merge to get wrong and no second definition of what
    // a comment is. The id is namespaced so it can never collide with a real bigint, and
    // `nestComments` places it under its parent by the SAME arithmetic it places every other row —
    // a pending reply is a reply.
    const sentBody = body;
    const sentShots = shots;
    // **AND THE REPLY TARGET IS SAVED WITH THEM, WHICH IS THE HALF THE DM SURFACE HAS NO EQUIVALENT
    // OF.** Clearing `answering` optimistically is right (the composer moves back to the bottom with
    // the words), but a refusal that restored her text and NOT her target would put her restored
    // words in the box as a TOP-LEVEL comment — the right sentence, filed under nobody, on the next
    // press. R57's law is that a refused send costs her nothing, and where a reply was going is part
    // of what she typed.
    const sentAnswering = answering;
    const pendingId = `pending-${Date.now()}`;
    const pending: Comment = {
      id: pendingId,
      body: sentBody,
      // A COMPLETE Identity or nothing — never a subset (R139: a hand-copied subset that quietly
      // loses a field renders a perfectly good row about the wrong person). The fallback is only
      // reachable before her own seat has loaded.
      author: me?.me ?? { ref: "", name: "", avatar: "", owner: false, crowned: false },
      mine: true,
      likes: 0,
      liked: false,
      createdAt: new Date().toISOString(),
      parentId,
      // Her pictures ride too, from the previews already in hand — a reply whose words appear
      // instantly and whose photographs appear three seconds later is half an optimistic render.
      media: sentShots.map((s, i) => ({ id: `${pendingId}-${i}`, kind: "image" as const, url: s.preview })),
      pending: true,
    };
    setComments((prev) => [...(prev || []), pending]);
    // Her reply has to be VISIBLE the moment it lands, and with the optimistic row that moment is
    // NOW rather than when the answer arrives: a thread already showing its two collapsed replies
    // would otherwise put her own words behind "View 3 more replies" for the whole round trip —
    // the exact defect this line was written for, one step earlier than it used to be reachable.
    if (parentId) setExpanded((prev) => new Set([...prev, parentId]));
    // The box clears WITH the optimistic row, because that is what "it appears in the thread" means
    // to the person typing. R57's law is kept by the refusal branch below, which puts everything back.
    setBody("");
    setShots([]);
    setStatus("");
    setAnswering(null);
    try {
      const d = await postJson(`/api/living-room/post/${postId}/comment`, {
        body: sentBody, parentId,
        media: sentShots.map((s) => ({ kind: "image", data: s.data, contentType: s.contentType })),
      });
      // R39/R47/R53: the ANSWER is read, never the status code. A refused send must never cost her
      // her words, her pictures or the thread she was answering — retyping a reply and re-choosing
      // four photographs is the whole cost of getting this wrong, and it is what the optimistic row
      // makes possible to lose. So a refusal takes the pending row back OUT and restores all three.
      if (!d?.ok) {
        setComments((prev) => (prev || []).filter((c) => c.id !== pending.id));
        setBody(sentBody);
        setShots(sentShots);
        setAnswering(sentAnswering);
        setStatus(d?.message || "That did not send.");
        return;
      }
      // IN PLACE: the server's list replaces ours and the pending row is gone with it. No
      // navigation, nothing opens, nothing closes. Held or clean, identical — a held comment is
      // returned to its author by the read's own predicate, so there is nothing here to know.
      setComments(d.comments || []);
      // HER CONFIRMATION, on the comment path too — the same field, the same sentence, and it is
      // present only when a ring really fired.
      setStatus(rangHouseNotice(d.rangHouse));
      onChanged();
    } finally {
      // **IN A `finally`, WHICH THE POST COMPOSER'S OWN `setBusy(false)` IS NOT.** `json()` swallows
      // everything and resolves `null`, so nothing here throws today — and this is the one control
      // in the room whose stuck state is a permanently dead Send button with her words still in it,
      // which is not a thing to leave resting on another module's catch (DL-2c, ITEM 13b).
      inFlight.current = false;
      setSending(false);
    }
  };

  // TAPPING REPLY, INSTAGRAM-EXACT: the box opens under that thread with @[that person] already in
  // it and the caret AFTER the tag. The focus runs on the next frame because the input has just been
  // moved to a different place in the tree and the node under `boxRef` is a new one.
  const openReply = (c: Comment) => {
    const next = replyComposeState(c.author?.name || "");
    setAnswering(c);
    setBody(next.text);
    setStatus("");
    requestAnimationFrame(() => {
      boxRef.current?.focus();
      boxRef.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  // ONE ROW, rendered at both levels. A reply is a comment, so it carries every control a comment
  // carries — like, edit, delete, report and Reply itself — rather than a reduced set that would
  // quietly make the second level a lesser kind of thing.
  const row = (c: Comment, isReply: boolean) => {
    // LR19 — HER REPORTED CASE, EXACTLY: *"a held comment looks completely NORMAL to owner eyes."*
    // Same law as the post, asked of the comment. Null for every member, including its author.
    const held = heldTreatment(c, { owner: me?.me?.owner === true });
    return (
    <div
      key={c.id}
      className={`${isReply ? "lr-comment lr-comment--reply" : "lr-comment"}${found === c.id ? " is-found" : ""}${c.pending ? " is-pending" : ""}${held ? " is-held" : ""}`}
      data-comment-id={c.id}
      data-reply={isReply ? "1" : undefined}
      data-held={held ? held.state : undefined}
    >
      <HeldPanel item={c} itemKind="comment" me={me} onChanged={onChanged} />
      <Who who={c.author} at={c.createdAt} edited={Boolean(c.editedAt)} size="sm"
        onOpen={onOpenMember && c.authorRef ? () => onOpenMember(c.authorRef as string) : undefined} />
      <MentionBody text={visibleBody(c, { ref: me?.me.ref })} mentions={c.mentions} className="lr-comment__body" />
      {/* ITEM 4(b): her pictures, inline, and every one of them opens full size in the shared viewer.
          A held comment's pictures are held with it — the server asks the COMMENT's visibility for
          both the list and the bytes, so there is nothing for this component to know about it.
          ITEM 7: the images-only filter is gone from the viewer's list here too — the index this
          call site passes counts the WHOLE strip, so the two now agree (R28/R29). */}
      {c.media?.length ? (
        <div className={`lr-media lr-media--${Math.min(c.media.length, 4)} lr-media--comment`}>
          {c.media.map((m, i) => (
            <button key={m.id} type="button" className="lr-media__open" aria-label="See this picture full size"
              onClick={() => setViewing({ items: c.media || [], index: i })}>
              <img src={m.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}
      {/* S1b — **A PENDING ROW OFFERS NOTHING TO PRESS.** `canActOn` is the shared law and the whole
          acts row goes with it, not merely the two controls that already ask a law: a like, a Reply
          and a flag all post an id that names no row, and the reply is the worst of the three (it
          would make `pending-…` a PARENT). It comes back with the server's answer, which is the
          same instant the id becomes real. */}
      {canActOn(c) ? (
      <span className="lr-comment__acts">
        <button type="button" className={c.liked ? "lr-tiny is-on" : "lr-tiny"} aria-label="Thumbs up"
          onClick={async () => {
            const d = await postJson("/api/living-room/like", { commentId: c.id });
            if (d?.ok) setComments((prev) => (prev || []).map((x) => (x.id === c.id ? { ...x, liked: d.liked, likes: d.likes } : x)));
          }}><ThumbsUp size={12} />{c.likes || ""}</button>
        {post.canComment !== false ? (
          <button type="button" className="lr-tiny lr-tiny--reply" aria-label={`Reply to ${c.author?.name || "this"}`}
            onClick={() => openReply(c)}>Reply</button>
        ) : null}
        {canEdit(c, { ref: me?.me.ref }) ? (
          <button type="button" className="lr-tiny" onClick={() => setEditing(c)} aria-label="Edit"><Pencil size={12} /></button>
        ) : null}
        {canDelete(c, { ref: me?.me.ref, owner: me?.me.owner }) ? (
          <button type="button" className="lr-tiny" onClick={() => setDeleting(c)} aria-label="Delete"><Trash2 size={12} /></button>
        ) : null}
        <button type="button" className="lr-tiny" aria-label="Report this reply" onClick={() => onReport({ commentId: c.id })}><Flag size={12} /></button>
      </span>
      ) : null}
    </div>
    );
  };

  // **THE CANCEL DOES NOT RIDE THE COMPOSE ROW, AND THE WALKTHROUGH IS WHY.** With the send button
  // and a cancel on one line inside an indented thread, the input measured 219px at 393px — under
  // the floor a conversation needs. It also reads better: a line saying WHO she is answering is the
  // thing a member wants there, and the way out belongs beside that rather than beside Send.
  const composer = (label: string) => (
    <div className="lr-replybox">
      {answering ? (
        <p className="lr-replybox__to">
          Replying to @{answering.author?.name || ""}
          <button type="button" className="lr-replybox__cancel"
            onClick={() => { setAnswering(null); setBody(""); }}>Cancel</button>
        </p>
      ) : null}
      {/* The pictures waiting to go with it, removable before she sends. */}
      {shots.length ? (
        <div className="lr-composer__media lr-composer__media--comment">
          {shots.map((s, i) => (
            <span key={i} className="lr-chip">
              <img src={s.preview} alt="" />
              <span className="lr-chip__name">{s.name}</span>
              <button type="button" aria-label="Remove" onClick={() => setShots((prev) => prev.filter((_, x) => x !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="lr-reply">
      <input
        ref={fileRef} type="file" accept={photoAccept()} multiple hidden
        onChange={(e) => { addShots(e.target.files); e.target.value = ""; }}
      />
      {/* THE PICTURE CONTROL RIDES INSIDE THE FIELD, and that is a measurement, not a taste: as its
          own column it cost the in-thread compose 42px and put the input at 219px on a 393px phone —
          the same squeeze the cancel button caused, found the same way. Inside the field it costs
          padding instead of width, which is also what every messaging app on her phone does. */}
      {/* HER CLAUSE 3 — **THE COMMENT BOX IS THE SAME COMPONENT, SO IT INHERITS EVERY CLAUSE BY
          EXISTING.** *"...and if the Living Room comment composer is the same component it inherits
          this too."* It was NOT the same component — it was a second single-row `<input>` with its
          own maxLength and its own Enter handler — so this round made it one. The picture control
          still rides INSIDE the field, which was a measurement rather than a taste (as its own column
          it cost the in-thread compose 42px and put the box at 219px on a 393px phone). */}
      <MessageComposer
        inputRef={boxRef} value={body} placeholder={label}
        max={me?.limits.commentChars || LIMITS.commentChars}
        onChange={(next, caret) => { setBody(next); picker.look(next, caret); }}
        onBlur={picker.dismiss}
        before={(
          <button type="button" className="lr-tiny lr-tiny--shot" aria-label="Add a picture"
            onClick={() => fileRef.current?.click()}><ImagePlus size={16} /></button>
        )}
        after={(
          <MentionPicker anchor={boxRef} people={picker.people} onPick={(name) => {
            const next = picker.choose(body, name);
            setBody(next.text);
            requestAnimationFrame(() => boxRef.current?.setSelectionRange(next.caret, next.caret));
          }} />
        )}
      />
      {/* D-5: `disabled` is the half she can SEE. The guard that decides is the ref inside `send`,
          because `disabled` is applied on the next render and three taps can land before one. */}
      <button type="button" className="lr-send" onClick={send} disabled={sending} aria-label="Send reply"><Send size={18} /></button>
      </div>
    </div>
  );

  const tree = nestComments(comments || []) as Comment[];
  // The thread the box is currently sitting in, so it is rendered ONCE — in that thread or at the
  // bottom, never both.
  const answeringRoot = answering ? replyParentFor(answering) : "";

  // ITEM 6 — PAST SIX ROWS THE LIST GETS ITS OWN SCROLL, AND THE COMPOSER STAYS OUTSIDE IT.
  //
  // The open test handed to the counter is the SAME expression the map below it uses, so the count
  // and the draw can never disagree — the alternative is two readings of "how tall is this" and
  // R28/R29 has been paid here more than once.
  //
  // **THE COMPOSER SITS OUTSIDE THE SCROLL BOX AND THAT IS THE WHOLE POINT OF THE WRAPPER.** Capping
  // `.lr-inline` itself would put her Say-something box inside the thing that scrolls, so a member
  // with forty comments would have to scroll a nested box to reach the field she opened the post to
  // use. The IN-THREAD reply composer stays inside on purpose: it belongs to the comment it is
  // answering and moves with it.
  const threadOpen = (c: Comment) => repliesOpen({
    replies: c.replies || [], expanded: expanded.has(c.id), wanted: highlightReply,
  });
  const scroll = commentScrollState(visibleCommentRows(tree, threadOpen));

  return (
    <div className="lr-inline">
      {/* FOUR STATES, AND THE ONE THAT WAS MISSING IS THE ONE THAT NEVER ENDS (R18/R64). A read that
          has not answered HOLDS on "Loading..."; one that FAILED says so ONCE, in the room's own
          sentence, so a member is never left under a promise that is not coming. */}
      {readFailed ? <p className="lr-note lr-note--failed">{status || ROOM_READ_FAILED}</p> : null}
      {!readFailed && comments === null ? <p className="lr-note">{COMMENTS_LOADING}</p> : null}

      <div className={scroll.scrolls ? "lr-inline__list lr-inline__list--scrolls" : "lr-inline__list"}
        data-comment-rows={scroll.rows}>
      {tree.map((c) => {
        const replies = c.replies || [];
        const open = repliesOpen({ replies, expanded: expanded.has(c.id), wanted: highlightReply });
        const toggle = repliesToggleLabel(replies.length, open);
        return (
          <div key={c.id} className="lr-thread">
            {row(c, false)}
            {replies.length ? (
              <div className="lr-thread__replies">
                {visibleReplies(replies, open).map((r) => row(r as Comment, true))}
                {toggle ? (
                  <button type="button" className="lr-thread__more" aria-expanded={open}
                    onClick={() => setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                      return next;
                    })}>{toggle}</button>
                ) : null}
              </div>
            ) : null}
            {post.canComment !== false && answeringRoot === c.id ? (
              <div className="lr-thread__compose">{composer(`Reply to ${answering?.author?.name || ""}`)}</div>
            ) : null}
          </div>
        );
      })}
      {comments !== null && !comments.length ? <p className="lr-empty">No replies yet.</p> : null}
      </div>

      {post.canComment !== false && !answering ? composer("Say something") : null}
      {status ? <p className="lr-status">{status}</p> : null}

      {viewing ? (
        <ImageViewer items={viewing.items} index={viewing.index} onClose={() => setViewing(null)} />
      ) : null}

      {editing ? (
        <EditSheet itemKind="comment" id={editing.id} initial={visibleBody(editing, { ref: me?.me.ref })}
          onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      ) : null}
      {deleting ? (
        <ConfirmDelete itemKind="comment" id={deleting.id}
          replies={(comments || []).filter((x) => String(x.parentId || "") === String(deleting.id)).length}
          onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); onChanged(); }} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// THE HOUSE RULES (LR1-3). Her rules, as the gate enforces them — the page and the gate read the
// same list, so this cannot promise a rule nothing checks.
// ---------------------------------------------------------------------------
function RulesSheet({ guidelines, onClose }: { guidelines: Me["guidelines"]; onClose: () => void }) {
  return (
    <Sheet label={guidelines.title} onClose={onClose} className="lr-modal--rules">
      <h2>{guidelines.title}</h2>
      <p className="lr-rules__intro">{guidelines.intro}</p>
      <ol className="lr-rules">
        {guidelines.rules.map((g) => (
          <li key={g.code}><strong>{g.rule}</strong><span>{g.body}</span></li>
        ))}
      </ol>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// LR1-12 ROUND THREE — THE FLAG CONFIRMATION. Her ruling replaced a category radio-list with this.
//
// The flag has ALREADY been sent by the time this renders: one tap, done. This is a confirmation,
// not a form, and the only thing it asks for is optional. If she taps away, the flag still stands —
// which is the difference between a toast and a modal, and the reason the words are optional rather
// than a second required step.
// ---------------------------------------------------------------------------
function FlagToast({
  toast, onClose,
}: { toast: { text: string; postId?: string; commentId?: string }; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState("");

  // It dismisses itself. A confirmation that needs to be closed is a modal wearing a smaller box.
  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(onClose, 4200);
    return () => window.clearTimeout(t);
  }, [open, onClose]);

  // R83 — the same defect one control down, and it is the one that made the fix worth doing here
  // rather than only at the flag: her WORDS were posted and *"Thank you. Lala has your words."* was
  // printed without the answer being read at all. It is a three-state label now (R56's ruling: a
  // boolean cannot say "it did not go") and the answer is what sets it.
  const sendNote = async () => {
    const d = await postJson("/api/living-room/report", { postId: toast.postId, commentId: toast.commentId, note });
    setSent(roomNote(d) || "Thank you. Lala has your words.");
  };

  // **AND THE OFFER IS WITHHELD WHEN THE FLAG ITSELF DID NOT LAND.** A refusal toast names no post
  // and no comment, so there is nothing for her words to be added TO — offering the box would post
  // a note about nothing and thank her for it.
  const flagged = Boolean(toast.postId || toast.commentId);

  return (
    <div className="lr-toast" role="status" aria-live="polite">
      <span className="lr-toast__text">{sent || toast.text}</span>
      {flagged && !sent && !open ? (
        <button type="button" className="lr-toast__more" onClick={() => setOpen(true)}>{REPORT_NOTE_PROMPT}</button>
      ) : null}
      {open && !sent ? (
        <div className="lr-toast__note">
          <input
            autoFocus value={note} maxLength={1000} aria-label="Why you flagged this"
            placeholder="A few words, if you want"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && note.trim()) sendNote(); }}
          />
          <button type="button" className="lr-send" aria-label="Send" onClick={() => {
            if (!note.trim()) { onClose(); return; }
            sendNote();
          }}><Send size={16} /></button>
        </div>
      ) : null}
      <button type="button" className="lr-toast__close" onClick={onClose} aria-label="Dismiss"><X size={14} /></button>
    </div>
  );
}
